import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { useUnreadCount } from '../../hooks';
import { getMenuItemsByRole } from '../../utils/roleConfig';
import { colors } from '../../theme/theme';
import { KChatAI } from '../KChatAI';

const drawerWidth = 280;
const collapsedDrawerWidth = 70;

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', new_password_confirmation: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, changePassword, isLoading: userLoading } = useAuth();
  const { count: unreadCount } = useUnreadCount();
  const theme = useTheme();

  // Get menu items based on user type, roles, and permissions
  const menuItems = useMemo(() => {
    const userRoles = user?.roles?.map((r: any) => r.name) || [];
    const userType = user?.user_type;
    // Collect all permission names from roles and direct permissions
    const userPermissions: string[] = [];
    user?.roles?.forEach((role: any) => {
      role.permissions?.forEach((p: any) => userPermissions.push(p.name));
    });
    user?.permissions?.forEach((p: any) => userPermissions.push(typeof p === 'string' ? p : p.name));
    return getMenuItemsByRole(userRoles, userType, userPermissions);
  }, [user]);

  const mustChangePassword = user?.must_change_password === true;

  const handleForcePasswordChange = async () => {
    setPasswordError('');
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      setPasswordError('Passwords do not match');
      return;
    }
    try {
      setPasswordSaving(true);
      await changePassword(passwordForm);
      // Update the stored user to clear must_change_password
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const stored = JSON.parse(userStr);
        stored.must_change_password = false;
        localStorage.setItem('user', JSON.stringify(stored));
      }
      window.location.reload();
    } catch (err: any) {
      setPasswordError(err?.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Auto-expand submenu if current URL matches a child item
  useEffect(() => {
    const currentPath = location.pathname;
    for (const item of menuItems) {
      if (item.children) {
        const match = item.children.some(child => child.path && currentPath.startsWith(child.path));
        if (match) {
          setOpenSubmenu(item.title);
          return;
        }
      }
    }
  }, [location.pathname, menuItems]);

  const handleSubmenuClick = (title: string) => {
    setOpenSubmenu(openSubmenu === title ? null : title);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRight: `1px solid ${colors.grey[200]}`,
        position: 'relative',
      }}
    >
      {/* Platform Header */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10,
          flexShrink: 0,
          background: colors.gradients.primary,
          color: 'white',
          p: sidebarCollapsed ? 1.5 : 3,
          transition: 'padding 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            right: -30,
            width: 120,
            height: 120,
            opacity: 0.15,
            background: 'white',
            borderRadius: '50%',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            position: 'relative',
            zIndex: 1,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          }}
        >
          {/* Toggle Button - Always Visible */}
          <IconButton
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
              flexShrink: 0,
              order: sidebarCollapsed ? 0 : 1,
            }}
            size="small"
          >
            {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>

          {/* K-Pay Logo Icon - Hide when collapsed */}
          {!sidebarCollapsed && (
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2.5,
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                fontSize: '1.5rem',
                background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                flexShrink: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  background: colors.gradients.primary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                K
              </Box>
            </Box>
          )}

          {/* Platform & County Name - Hide when collapsed */}
          {!sidebarCollapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {user && user.user_type === 'county' ? (
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                      fontSize: '1.5rem',
                      letterSpacing: '-1.5px',
                      color: 'white',
                      mb: 0.5,
                    }}
                  >
                    K-Pay
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        height: 12,
                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: 1,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        fontSize: '0.95rem',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user?.county?.name || 'County'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                      fontSize: '1.5rem',
                      letterSpacing: '-1.5px',
                      color: 'white',
                      mb: 0.5,
                    }}
                  >
                    K-Pay
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Revenue Platform
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ borderColor: colors.grey[200], flexShrink: 0 }} />

      {/* Menu Items - Hide when collapsed */}
      {!sidebarCollapsed && (
        <List
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 2,
            flexGrow: 1,
            flexShrink: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            zIndex: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: colors.grey[300],
              borderRadius: '10px',
              '&:hover': {
                bgcolor: colors.grey[400],
              },
            },
          }}
        >
          {userLoading ? (
            // Skeleton loader for sidebar
            [...Array(8)].map((_, i) => (
              <Box key={i} sx={{ px: 2, py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2, px: 1.5 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                  {!sidebarCollapsed && <Skeleton variant="text" width={`${60 + Math.random() * 30}%`} height={20} />}
                </Box>
              </Box>
            ))
          ) : menuItems.map((item, index) => (
          <Box
            key={item.title}
            sx={{
              mb: 1,
              animation: `slideIn 0.3s ease-out ${index * 0.05}s backwards`,
              '@keyframes slideIn': {
                from: {
                  opacity: 0,
                  transform: 'translateX(-10px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          >
            {item.children ? (
              (() => {
                const hasActiveChild = item.children.some(child => child.path && location.pathname.startsWith(child.path));
                return (
                  <>
                    <Tooltip title={sidebarCollapsed ? item.title : ''} placement="right" arrow>
                      <ListItemButton
                        onClick={() => handleSubmenuClick(item.title)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          py: 1.5,
                          px: sidebarCollapsed ? 1.5 : 2,
                          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                          bgcolor: hasActiveChild ? colors.primary.main : openSubmenu === item.title ? colors.primary[50] : 'transparent',
                          boxShadow: hasActiveChild ? colors.shadows.button : 'none',
                          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                          '&:hover': {
                            backgroundColor: hasActiveChild ? colors.primary.dark : colors.primary[50],
                            transform: sidebarCollapsed ? 'scale(1.1)' : 'translateX(4px)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 'auto' : 40, justifyContent: 'center' }}>
                          {item.icon && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: hasActiveChild ? 'white' : colors.primary.main, transition: 'color 200ms' }}>
                              {item.icon}
                            </Box>
                          )}
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                          <>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: '0.9375rem',
                                fontWeight: hasActiveChild || openSubmenu === item.title ? 700 : 600,
                                color: hasActiveChild ? 'white' : colors.grey[700],
                              }}
                            />
                            {openSubmenu === item.title ? (
                              <ExpandLess sx={{ color: hasActiveChild ? 'white' : colors.primary.main }} />
                            ) : (
                              <ExpandMore sx={{ color: hasActiveChild ? 'rgba(255,255,255,0.7)' : colors.grey[500] }} />
                            )}
                          </>
                        )}
                      </ListItemButton>
                    </Tooltip>
                    <Collapse in={openSubmenu === item.title && !sidebarCollapsed} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ mt: 0.5 }}>
                        {item.children.map((child, childIndex) => {
                          const isActive = child.path ? location.pathname.startsWith(child.path) : false;
                          return (
                            <ListItemButton
                              key={child.path}
                              sx={{
                                pl: 2.5,
                                pr: 2,
                                py: 1,
                                borderRadius: 2,
                                mb: 0.25,
                                ml: 1.5,
                                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                                backgroundColor: 'transparent',
                                bgcolor: isActive ? `${colors.primary.main}10` : 'transparent',
                                animation: `fadeIn 0.2s ease-out ${childIndex * 0.05}s backwards`,
                                '@keyframes fadeIn': {
                                  from: { opacity: 0, transform: 'translateY(-5px)' },
                                  to: { opacity: 1, transform: 'translateY(0)' },
                                },
                                '&:hover': {
                                  backgroundColor: colors.primary[50],
                                  transform: 'translateX(4px)',
                                },
                              }}
                              onClick={() => child.path && handleNavigate(child.path)}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {child.icon && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: isActive ? colors.primary.main : colors.grey[500], transition: 'color 200ms', '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
                                    {child.icon}
                                  </Box>
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={child.title}
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: isActive ? 700 : 500,
                                  color: isActive ? colors.primary.main : colors.grey[600],
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                );
              })()
            ) : (
              <ListItemButton
                onClick={() => item.path && handleNavigate(item.path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 1.5,
                  px: 2,
                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: location.pathname === item.path
                    ? colors.primary.main
                    : 'transparent',
                  color: location.pathname === item.path ? 'white' : 'inherit',
                  boxShadow: location.pathname === item.path ? colors.shadows.button : 'none',
                  '&:hover': {
                    backgroundColor: location.pathname === item.path
                      ? colors.primary.dark
                      : colors.primary[50],
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: location.pathname === item.path ? 'white' : colors.primary.main,
                        transition: 'color 200ms',
                      }}
                    >
                      {item.icon}
                    </Box>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.9375rem',
                    fontWeight: location.pathname === item.path ? 700 : 600,
                    color: location.pathname === item.path ? 'white' : colors.grey[700],
                  }}
                />
              </ListItemButton>
            )}
          </Box>
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)',
          backdropFilter: 'blur(10px)',
          color: 'text.primary',
          boxShadow: `0 2px 8px ${colors.primary.main}14`,
          borderBottom: `1px solid ${colors.primary.main}14`,
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' },
              bgcolor: `${colors.primary.main}14`,
              '&:hover': { bgcolor: `${colors.primary.main}26` },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Page Title */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                fontSize: '1.5rem',
                letterSpacing: '-0.5px',
              }}
            >
              {menuItems.find(m => m.path === location.pathname)?.title ||
               menuItems.flatMap(m => m.children || []).find(c => c.path === location.pathname)?.title ||
               'Dashboard'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontSize: '0.8125rem',
                fontWeight: 500,
              }}
            >
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Notifications */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/notifications')}
              sx={{
                bgcolor: `${colors.primary.main}14`,
                width: 44,
                height: 44,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: `${colors.primary.main}26`,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  },
                }}
              >
                <NotificationsIcon sx={{ color: colors.primary.main }} />
              </Badge>
            </IconButton>

            {/* Profile */}
            <Box
              onClick={handleProfileMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                py: 0.75,
                px: 1.5,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: `${colors.primary.main}14`,
                },
              }}
            >
              <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#1f2937',
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                  }}
                >
                  {user?.name || 'Admin User'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontSize: '0.75rem',
                  }}
                >
                  {user?.roles?.[0]?.name || 'Administrator'}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: `0 4px 12px ${colors.primary.main}4D`,
                }}
              >
                {user?.name?.charAt(0) || 'A'}
              </Avatar>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: `0 8px 24px ${colors.primary.main}26`,
            border: `1px solid ${colors.primary.main}14`,
            overflow: 'hidden',
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 2, bgcolor: `${colors.primary.main}0A` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                fontWeight: 700,
              }}
            >
              {user?.name?.charAt(0) || 'A'}
            </Avatar>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#1f2937',
                  fontSize: '0.9375rem',
                }}
              >
                {user?.name || 'Admin User'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  fontSize: '0.75rem',
                }}
              >
                {user?.email || 'admin@county.gov'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 0 }} />

        <MenuItem
          onClick={() => { handleMenuClose(); navigate('/profile'); }}
          sx={{
            py: 1.5,
            px: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: `${colors.primary.main}14`,
              transform: 'translateX(4px)',
            },
          }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" sx={{ color: colors.primary.main }} />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <Divider sx={{ my: 0 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.5,
            px: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(231, 29, 54, 0.08)',
              transform: 'translateX(4px)',
              '& .MuiListItemIcon-root': {
                color: '#e71d36',
              },
              '& .MuiTypography-root': {
                color: '#e71d36',
              },
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: '#64748b' }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s ease',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: {
            sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)`,
          },
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          maxHeight: '100vh',
          overflow: 'auto',
          transition: 'width 0.3s ease',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, p: 3 }}>
          {children}
        </Box>

        {/* K-Pay Footer */}
        <Box
          component="footer"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            borderTop: `1px solid ${colors.grey[200]}`,
            mt: 'auto',
          }}
        >
          {/* Decorative gradient blurs */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '100%',
              background: colors.gradients.overlay,
              opacity: 0.5,
              pointerEvents: 'none',
            }}
          />

          <Box sx={{
            position: 'relative',
            zIndex: 1,
            py: 4,
            px: 3,
          }}>
            {/* Main Footer Content */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 3,
              mb: 3,
            }}>
              {/* Left side - Branding */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: colors.gradients.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1rem',
                    color: 'white',
                    boxShadow: colors.shadows.button,
                  }}
                >
                  K
                </Box>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      letterSpacing: '-1px',
                      lineHeight: 1.2,
                    }}
                  >
                    <Box component="span" sx={{ color: colors.brand.kDark }}>K-</Box>
                    <Box component="span" sx={{ color: colors.primary.main }}>Pay</Box>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey[600],
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    County Revenue Management
                  </Typography>
                </Box>
              </Box>

              {/* Right side - Support Link */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
              }}>
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => { e.preventDefault(); setChatOpen(true); }}
                  sx={{
                    color: colors.grey[600],
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: colors.primary.main,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Support
                </Typography>
              </Box>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                height: '1px',
                background: `linear-gradient(to right, transparent, ${colors.grey[300]}, transparent)`,
                mb: 3,
              }}
            />

            {/* Bottom Section */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.grey[500],
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                }}
              >
                © {new Date().getFullYear()} K-Pay. All rights reserved.
              </Typography>

              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => { e.preventDefault(); }}
                  sx={{
                    color: colors.grey[500],
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: colors.grey[700],
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Terms of Service
                </Typography>
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => { e.preventDefault(); }}
                  sx={{
                    color: colors.grey[500],
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: colors.grey[700],
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Privacy Policy
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* K-Chat AI Assistant */}
      <KChatAI isOpen={chatOpen} onToggle={() => setChatOpen(!chatOpen)} />

      {/* Force Password Change Dialog */}
      <Dialog open={mustChangePassword} maxWidth="sm" fullWidth
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}>
        <DialogTitle>Change Your Password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You must change your password before continuing. This is required for security purposes.
          </Alert>
          {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth required type="password" label="Current / Temporary Password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} />
            <TextField fullWidth required type="password" label="New Password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              helperText="Minimum 8 characters" />
            <TextField fullWidth required type="password" label="Confirm New Password"
              value={passwordForm.new_password_confirmation}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password_confirmation: e.target.value })}
              error={passwordForm.new_password_confirmation.length > 0 && passwordForm.new_password !== passwordForm.new_password_confirmation}
              helperText={passwordForm.new_password_confirmation.length > 0 && passwordForm.new_password !== passwordForm.new_password_confirmation ? 'Passwords do not match' : ''} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleForcePasswordChange}
            disabled={passwordSaving || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.new_password_confirmation}
            startIcon={passwordSaving ? <CircularProgress size={20} /> : undefined}>
            {passwordSaving ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
